import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import config from '@/config';
import ArtlistDataContext from '@/context/ArtlistDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import GoBackButton from '@/shared-components/buttons/components/GoBackButton';
import Loader from '@/shared-components/loader/Loader';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';

import ArtlistHeader from './components/ArtlistHeader';
import ArtlistNotFound from './components/ArtlistNotFound';
import ArtlistShareButton from './components/ArtlistShareButton';
import ArtlistBody from './artlist-body';

import './index.css';

function normalizeString(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function truncateText(value, maxLength = 160) {
  const normalized = normalizeString(value);
  if (!normalized || normalized.length <= maxLength) {
    return normalized;
  }

  const shortened = normalized.slice(0, Math.max(0, maxLength - 1)).trim();
  const lastSpaceIndex = shortened.lastIndexOf(' ');
  const safeValue = lastSpaceIndex > 80 ? shortened.slice(0, lastSpaceIndex) : shortened;
  return `${safeValue.trim()}...`;
}

function resolveArtlistImageUrl(artlist = {}) {
  const imageUrl = normalizeString(artlist?.artlist_image?.image_url);
  if (imageUrl) {
    return imageUrl;
  }

  const imageId = normalizeString(artlist?.artlist_image?.image_id);
  if (imageId) {
    return `https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/${imageId}/w=1200`;
  }

  return undefined;
}

function normalizeArtlistData(artlistData) {
  if (!artlistData || typeof artlistData !== 'object') {
    return null;
  }

  return {
    ...artlistData,
    artlist_creator: artlistData.artlist_creator ?? {},
    artlist_artworks: Array.isArray(artlistData.artlist_artworks)
      ? artlistData.artlist_artworks
      : [],
  };
}

export default function Artlist() {
  const { state } = useStateHandler();
  const t = useTranslations('common.profile.pages.artlist');
  const { artlistId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, fetchData, setFetchData } = useCache(
    `artlist_${artlistId}_view`,
    `${config.apis.api.url}/artlist/${artlistId}`,
    {
      injectToken: true,
      invalidateWhen: ["ADD_TO_ARTLIST_FROM_ARTWORK", "DELETE_ARTLIST", "EDIT_ARTLIST"],
    }
  );

  const [openAddArtworks, setOpenAddArtworks] = useState(false);
  const [shouldAutoOpenTimeSelector] = useState(() => Boolean(location.state?.openTimeSelector));

  useEffect(() => {
    if (location.state?.openTimeSelector) {
      // eslint-disable-next-line no-unused-vars
      const { openTimeSelector: _omit, ...rest } = location.state;
      const nextState = Object.keys(rest).length ? rest : undefined;
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: nextState }
      );
    }
  }, [location, navigate]);

  const artlistData = normalizeArtlistData(fetchData);
  const hasValidCreator = Boolean(artlistData?.artlist_creator?._id);

  if (loading) {
    return (
      <div className="artlist-page">
        <GoBackButton />
        <div className="artlist-page__container-wrap">
          <div className="artlist-page__loading-page" role="status">
            <Loader className="artlist-page__spinner" thin />
          </div>
        </div>
      </div>
    );
  }

  if (fetchData?.error_type === "NOT_FOUND" || !hasValidCreator) {
    return (
      <div className="artlist-page">
        <GoBackButton />
        <div className="artlist-page__container-wrap">
          <ArtlistNotFound />
        </div>
      </div>
    );
  }

  const artlistTitle = artlistData.artlist_title || 'Artlist';
  const creatorUsername = artlistData.artlist_creator.user_username || 'User';
  const totalArtworks = artlistData.artlist_artworks_total ?? artlistData.artlist_artworks.length;
  const canonicalUrl = `https://suarte.art/artlist/${artlistData._id || artlistId}`;
  const robots = totalArtworks >= 5
    ? 'index,follow,max-image-preview:large'
    : 'noindex,follow,max-image-preview:large';
  const title = t('meta.title', {
    artlistTitle,
    username: creatorUsername,
  });
  const fallbackDescription = t('meta.description', {
    username: creatorUsername,
    count: totalArtworks,
  });
  const description = truncateText(artlistData.artlist_description || fallbackDescription);

  return (
    <>
      <StaticSeoHead
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        robots={robots}
        image={resolveArtlistImageUrl(artlistData)}
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Artlists', url: 'https://suarte.art/artlists' },
          { name: artlistTitle, url: canonicalUrl },
        ]}
      />
      <ArtlistDataContext.Provider
        value={{
          artlistData,
          setArtlistData: setFetchData,
          isCreator: state.user_session?._id === artlistData.artlist_creator._id,
        }}
      >
        <div className="artlist-page">
          <div className="artlist-page__top-actions">
            <GoBackButton />
            <ArtlistShareButton />
          </div>

          <div className="artlist-page__container-wrap">
            <ArtlistHeader
              openAddArtworks={openAddArtworks}
              setOpenAddArtworks={setOpenAddArtworks}
              autoOpenTimeSelector={shouldAutoOpenTimeSelector}
            />

            <ArtlistBody
              cacheKey={`artlist_${artlistId}_view`}
              openAddArtworks={openAddArtworks}
              setOpenAddArtworks={setOpenAddArtworks}
            />
          </div>
        </div>
      </ArtlistDataContext.Provider>
    </>
  );
}
